import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './pages/auth/LoginPage';
import AppLayout from './layouts/AppLayout';
import LenderDashboardPage from './pages/dashboard/LenderDashboardPage';
import BrokersListPage from './pages/organization/BrokersListPage';
import BrokerDetailPage from './pages/organization/BrokerDetailPage';
import FeatureManagementPage from './pages/admin/FeatureManagementPage';
import LockDeskSettingsPage from './pages/admin/LockDeskSettingsPage';
import LenderUsersPage from './pages/organization/LenderUsersPage';
import VestaContactSyncPage from './pages/admin/VestaContactSyncPage';
import BrokerPipelinePage from './pages/pipeline/BrokerPipelinePage';
import CompensationPage from './pages/organization/CompensationPage';
import BrokerManageCompanyPage from './pages/organization/BrokerManageCompanyPage';
import LoanDetailPage from './pages/loan/LoanDetailPage';
import ScenarioPricerPage from './pages/pricing/ScenarioPricerPage';
import BorrowerPortalPage from './pages/public/BorrowerPortalPage';
import ActivateAccountPage from './pages/auth/ActivateAccountPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import BrokerSignupPage from './pages/auth/BrokerSignupPage';
import AeDashboardPage from './pages/dashboard/AeDashboardPage';
import RetailPipelinePage from './pages/pipeline/RetailPipelinePage';
import ProtectedRoute from './components/ProtectedRoute';
import { Roles } from './constants';
import { BROKER_PORTAL_ROLES } from './config/brokerAccess';

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<LoginPage />} />

        {/* Public Routes */}
        <Route path="/auth/:userId/confirm" element={<ActivateAccountPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/signup" element={<BrokerSignupPage />} />
        <Route path="/borrower-portal" element={<BorrowerPortalPage />} />

        {/* Lender / Admin Routes */}
        <Route path="/lender" element={
          <ProtectedRoute allowedRoles={[Roles.Admin, Roles.LenderAdmin, Roles.AccountExecutive]}>
            <AppLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/lender/dashboard" replace />} />
          <Route path="dashboard" element={<LenderDashboardPage />} />
          <Route path="brokers" element={<BrokersListPage />} />
          <Route path="brokers/:id" element={<BrokerDetailPage />} />
          <Route path="users" element={<LenderUsersPage />} />
          <Route path="vesta-contacts" element={<VestaContactSyncPage />} />
          <Route path="retail-pipeline" element={<RetailPipelinePage />} />
          <Route path="scenario-pricer" element={<ScenarioPricerPage />} />
          <Route path="retail-pipeline/:id" element={<LoanDetailPage />} />
          <Route path="lock-desk" element={
            <ProtectedRoute allowedRoles={[Roles.Admin, Roles.LenderAdmin]}>
              <LockDeskSettingsPage />
            </ProtectedRoute>
          } />
          <Route path="feature-management" element={
            <ProtectedRoute allowedRoles={[Roles.Admin]}>
              <FeatureManagementPage />
            </ProtectedRoute>
          } />
        </Route>

        {/* AE (Account Executive) Routes */}
        <Route path="/ae" element={
          <ProtectedRoute allowedRoles={[Roles.Admin, Roles.LenderAdmin, Roles.LoanOfficer, Roles.AccountExecutive]}>
            <AppLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/ae/dashboard" replace />} />
          <Route path="dashboard" element={<AeDashboardPage />} />
          <Route path="pipeline" element={<AeDashboardPage />} />
          <Route path="scenario-pricer" element={<ScenarioPricerPage />} />
          <Route path="loans/:id" element={<LoanDetailPage />} />
        </Route>

        {/* Broker Routes — read-only users: pipeline + loan view only; mutations require BrokerAdmin/Broker/Processor */}
        <Route path="/broker" element={
          <ProtectedRoute allowedRoles={BROKER_PORTAL_ROLES}>
            <AppLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/broker/pipeline" replace />} />
          <Route path="company" element={
            <ProtectedRoute allowedRoles={[Roles.BrokerAdmin, Roles.Processor]}>
              <BrokerManageCompanyPage />
            </ProtectedRoute>
          } />
          <Route path="scenario-pricer" element={
            <ProtectedRoute allowedRoles={BROKER_PORTAL_ROLES}>
              <ScenarioPricerPage />
            </ProtectedRoute>
          } />
          <Route path="pipeline" element={<BrokerPipelinePage />} />
          <Route path="loans/:id" element={<LoanDetailPage />} />
          <Route path="compensation" element={
            <ProtectedRoute allowedRoles={[Roles.BrokerAdmin]}>
              <CompensationPage />
            </ProtectedRoute>
          } />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
